import html2canvas from 'html2canvas';

export const APP_MAIN_CONTENT_ID = 'app-main-content';

const MAX_CAPTURE_WIDTH = 1920;

function pngDataUrlToPayload(dataUrl: string): { imageBase64: string; mimeType: string } {
	const i = dataUrl.indexOf(',');
	const base64 = i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
	return { imageBase64: base64, mimeType: 'image/png' };
}

async function captureViaDisplayMedia(): Promise<{ imageBase64: string; mimeType: string }> {
	const opts: MediaStreamConstraints & { preferCurrentTab?: boolean } = {
		video: true,
		audio: false,
		preferCurrentTab: true,
	};
	const stream = await navigator.mediaDevices.getDisplayMedia(opts);
	try {
		const video = document.createElement('video');
		video.playsInline = true;
		video.muted = true;
		video.srcObject = stream;
		await video.play();
		await new Promise<void>((resolve) => {
			if (video.readyState >= 2) {
				resolve();
			} else {
				video.onloadeddata = () => resolve();
			}
		});
		await new Promise((r) => setTimeout(r, 200));
		const vw = video.videoWidth;
		const vh = video.videoHeight;
		if (!vw || !vh) {
			throw new Error('Could not read capture dimensions');
		}
		const canvas = document.createElement('canvas');
		let cw = vw;
		let ch = vh;
		if (vw > MAX_CAPTURE_WIDTH) {
			const s = MAX_CAPTURE_WIDTH / vw;
			cw = Math.round(vw * s);
			ch = Math.round(vh * s);
		}
		canvas.width = cw;
		canvas.height = ch;
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			throw new Error('Canvas not supported');
		}
		ctx.drawImage(video, 0, 0, vw, vh, 0, 0, cw, ch);
		return pngDataUrlToPayload(canvas.toDataURL('image/png'));
	} finally {
		stream.getTracks().forEach((t) => t.stop());
	}
}

async function captureViaHtml2Canvas(el: HTMLElement): Promise<{ imageBase64: string; mimeType: string }> {
	const scale = Math.min(1, 1200 / Math.max(el.scrollWidth, 1));
	const canvas = await html2canvas(el, {
		useCORS: true,
		allowTaint: true,
		scale,
		logging: false,
		backgroundColor: '#ffffff',
	});
	return pngDataUrlToPayload(canvas.toDataURL('image/png'));
}

export async function captureAppMainScreenshot(): Promise<{ imageBase64: string; mimeType: string }> {
	const el = document.getElementById(APP_MAIN_CONTENT_ID);
	if (!el) {
		throw new Error('Main content not found');
	}

	const canUseDisplayMedia =
		typeof window !== 'undefined' &&
		window.isSecureContext &&
		navigator.mediaDevices &&
		typeof navigator.mediaDevices.getDisplayMedia === 'function';

	if (canUseDisplayMedia) {
		try {
			return await captureViaDisplayMedia();
		} catch (err: unknown) {
			const name =
				err && typeof err === 'object' && 'name' in err ? String((err as { name: string }).name) : '';
			if (name === 'NotAllowedError' || name === 'AbortError') {
				throw new Error(
					'Screen capture was cancelled. When prompted, allow sharing and choose this browser tab so the dashboard is visible.'
				);
			}
			if (name === 'NotSupportedError') {
				return captureViaHtml2Canvas(el);
			}
			throw err;
		}
	}

	return captureViaHtml2Canvas(el);
}
